import { removeSuccessive } from '@maxpreps/shared/functions/primitiveHelpers/arrayHelpers';
import { isNumeric } from '@maxpreps/shared/functions/primitiveHelpers/numberHelpers';
import {
  IPageMetaData,
  IProjectMetaData
} from '@maxpreps/shared/models/metaTypes';
import { ISlotPayload } from '../../redux/bidbarrel/bidBarrelHooks';
import { adData as globalAdData } from '../app/global.meta';
import getNextConfig from './../../constants/getNextConfig';
import { DeviceType } from './requestContextUtils';
import { getUserDataFromMemberCookie } from './userUtils';

const { appEnv } = getNextConfig().publicRuntimeConfig;

const DFP_REGION = 'aw';
const DFP_PROPERTY = 'maxpreps';

const PROPERTY_MAP: { api: string; tracking: string }[] = [
  {
    api: 'gender',
    tracking: 'gender'
  },
  {
    api: 'sport',
    tracking: 'sport'
  },
  {
    api: 'schoolId',
    tracking: 'schoolId'
  },
  {
    api: 'teamId',
    tracking: 'schoolId'
  },
  {
    api: 'stateCode',
    tracking: 'state'
  },
  {
    api: 'state',
    tracking: 'state'
  }
];

const parseData = data => {
  if (!data) return {};
  const formattedData = {};
  PROPERTY_MAP.forEach(map => {
    if (typeof data[map.api] !== 'undefined') {
      formattedData[map.tracking] = data[map.api];
    }
  });
  return formattedData;
};

interface IFormattedMemberCookie {
  ug?: string;
  uby?: string;
  role?: string;
}

const parseMemberCookie = (cookieData): IFormattedMemberCookie => {
  if (typeof window === 'undefined') return {};

  let { puid, birthdate, role } = cookieData;

  const formattedData: IFormattedMemberCookie = {};

  if (puid) {
    formattedData.ug = puid.toLowerCase().indexOf('f') !== -1 ? '1' : '0';
  }

  if (birthdate) {
    birthdate = birthdate.split('/');

    if (birthdate.length > 2) {
      if (isNumeric(birthdate[2])) {
        formattedData.uby = (parseInt(birthdate) + 456).toString();
      }
    }
  }

  if (role) {
    formattedData.role = role.replace(' ', '_');
  }

  return formattedData;
};

const buildDfpPath = (envName, device, pagePath, isAdmin) => {
  const dfpServer = envName === 'dev' ? '7336' : '8264';
  const dfpDevice = device === 'mobile' ? 'm' : '';
  const admin = isAdmin ? '/admin' : '';

  return `/${dfpServer}/${dfpDevice}${DFP_REGION}-${DFP_PROPERTY}/${pagePath}${admin}`;
};

export interface IBidBarrelConfig {
  dfpPath: string;
  targeting: {
    auto: boolean;
    cookie: {
      consolidate: boolean;
      domain: string;
    };
  };
  pageTargeting: {
    env: string;
    vguid: string;
    ug?: string;
    uby?: string;
    role?: string;
  };
  firstPartyData: {
    eid?: string;
  };
}

export const formatConfig = (
  projectMetaData: IProjectMetaData,
  pageMetaData: IPageMetaData,
  // intended to pass in things like maxfeat, gender, sports, ect...
  contextDataPoints: any,
  pageViewGuid: string,
  deviceType: DeviceType,
  isAdmin: boolean
): IBidBarrelConfig => {
  const envName = appEnv === 'dev' || appEnv === 'development' ? 'dev' : 'prod';

  const pagePath = [
    // @ts-ignore
    ...(pageMetaData?.hierarchy ||
      (projectMetaData?.hierarchy && [projectMetaData.hierarchy]))
  ];

  const dfpPath = buildDfpPath(
    envName,
    deviceType,
    removeSuccessive(pagePath).join('/'),
    isAdmin
  );

  const userCookieData = getUserDataFromMemberCookie();

  return {
    dfpPath,
    targeting: {
      auto: true,
      cookie: {
        consolidate: true,
        domain: '.maxpreps.com'
      }
    },
    pageTargeting: {
      env: envName,
      vguid: pageViewGuid,
      ...globalAdData?.targeting,
      ...(pageMetaData?.targeting ?? {}),
      ...parseData(contextDataPoints ?? {}),
      ...parseMemberCookie(userCookieData)
    },
    firstPartyData: {
      eid: userCookieData.email ?? undefined
    }
  };
};

export interface IAdSlot {
  id: string;
  incIndex?: number;
  isOutOfPage: boolean;
}

export const isSlotEqual = (a: IAdSlot, b: IAdSlot): boolean => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.id === b.id && a.incIndex === b.incIndex;
};

export const filterDuplicateSlots = (slots: IAdSlot[]): any[] =>
  slots.filter(
    (value, index, array) =>
      array.findIndex(slot => isSlotEqual(value, slot)) === index
  );

/**
 * https://at.cbsi.com/lib/docs/api/#BidBarrel.auction
 * https://at.cbsi.com/lib/docs/api/#BidBarrel~AdUnit
 */
export const getSlotPayload = (slots: IAdSlot[]): ISlotPayload[] =>
  slots.map(slot => {
    if (slot.incIndex)
      return [
        slot.id,
        slot.incIndex,
        { pos: 'inc' },
        {
          //{ cache: true } // toggles lazy loading on programatically
          outOfPage: !!slot.isOutOfPage
        }
      ];
    return slot.id;
  });
